<template>
  <div class="debug-race-creator">
    <div class="debug-race-creator__header">
      <h3>🏁 Race Creator Debug</h3>
      <p class="debug-race-creator__subtitle">Create Test Races for Development</p>
    </div>
    
    <div class="debug-race-creator__content">
      <!-- Create Test Races Section -->
      <div class="debug-section">
        <h4>🚀 Bulk Race Creation</h4>
        <div class="debug-form-row">
          <div class="debug-form-group">
            <label class="debug-label">Number of Races:</label>
            <input 
              type="number" 
              v-model="numberOfRaces" 
              class="debug-input"
              placeholder="Enter number of races to create"
              min="1"
              max="50"
              @input="clearMessages"
            />
          </div>
          <div class="debug-form-group">
            <label class="debug-label">Duration (minutes):</label>
            <input 
              type="number" 
              v-model="raceDuration" 
              class="debug-input"
              placeholder="Race duration in minutes"
              min="1"
              max="120"
              @input="clearMessages"
            />
          </div>
        </div>
        <div class="debug-form-row">
          <div class="debug-form-group debug-form-group--full">
            <label class="debug-label">Race Date:</label>
            <date-field 
              v-model="selectedDate" 
              name="raceDate"
              @input="clearMessages"
            ></date-field>
          </div>
        </div>
        
        <div class="debug-buttons">
          <button 
            class="debug-btn debug-btn--primary" 
            @click="createTestRaces"
            :disabled="loading || !numberOfRaces || !raceDuration || !selectedDate"
          >
            <span v-if="loading">Creating {{ currentRaceIndex }}/{{ numberOfRaces }}...</span>
            <span v-else>Create Test Races</span>
          </button>
          <button 
            class="debug-btn debug-btn--secondary" 
            @click="clearAll"
            :disabled="loading"
          >
            Clear Form
          </button>
        </div>

        <div v-if="successMessage" class="debug-alert debug-alert--success">
          {{ successMessage }}
        </div>
        <div v-if="errorMessage" class="debug-alert debug-alert--error">
          {{ errorMessage }}
        </div>
        <div v-if="progressMessages.length > 0" class="debug-progress">
          <h5>Creation Progress:</h5>
          <ul class="debug-progress-list">
            <li v-for="(message, index) in progressMessages" :key="index" class="debug-progress-item">
              {{ message }}
            </li>
          </ul>
        </div>
      </div>

      <!-- Configuration Preview -->
      <div class="debug-section">
        <h4>⚙️ Race Configuration Preview</h4>
        <div class="debug-config-preview">
          <div class="debug-config-item">
            <span class="debug-config-label">Name Pattern:</span>
            <span class="debug-config-value">TEST_DO_NOT_JOIN_1, TEST_DO_NOT_JOIN_2, ...</span>
          </div>
          <div class="debug-config-item">
            <span class="debug-config-label">Date:</span>
            <span class="debug-config-value">{{ formattedSelectedDate || 'Not set' }}</span>
          </div>
          <div class="debug-config-item">
            <span class="debug-config-label">Type:</span>
            <span class="debug-config-value">LIVE (Medal)</span>
          </div>
          <div class="debug-config-item">
            <span class="debug-config-label">Duration:</span>
            <span class="debug-config-value">{{ raceDuration || 'Not set' }} minutes</span>
          </div>
          <div class="debug-config-item">
            <span class="debug-config-label">Description:</span>
            <span class="debug-config-value">Test race created by debug tool - DO NOT JOIN</span>
          </div>
          <div class="debug-config-item">
            <span class="debug-config-label">Image URL:</span>
            <span class="debug-config-value debug-config-value--small">
              https://sgp1.vultrobjects.com/liverun-bucket/spreaker-live/race-image/fc07872c-2254-4228-a9f5-6ba0eef9edd8.png
            </span>
          </div>
        </div>
      </div>

      <!-- Debug Info -->
      <div class="debug-section">
        <h4>📊 Debug Information</h4>
        <div class="debug-info">
          <div class="debug-info-item">
            <span class="debug-label">Trainer ID:</span>
            <span class="debug-value">{{ trainer ? trainer.id : 'Loading...' }}</span>
          </div>
          <div class="debug-info-item">
            <span class="debug-label">API Endpoint:</span>
            <span class="debug-value debug-value--small">{{ apiEndpoint }}</span>
          </div>
          <div class="debug-info-item">
            <span class="debug-label">Current Time:</span>
            <span class="debug-value">{{ currentTime }}</span>
          </div>
          <div class="debug-info-item">
            <span class="debug-label">Debug Mode:</span>
            <span class="debug-value debug-value--active">Active</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import DateField from '@/components/Forms/DateField'
import SettingsService from "@/services/settings"
import { db } from '../../firebase'

export default {
  name: 'DebugRaceCreator',
  components: {
    DateField
  },
  data() {
    // Default to 6 months from today
    const defaultDate = new Date()
    defaultDate.setMonth(defaultDate.getMonth() + 6)
    const defaultDateString = defaultDate.toISOString().split('T')[0] // Format as YYYY-MM-DD
    
    return {
      currentTime: new Date().toLocaleTimeString(),
      numberOfRaces: 5,
      raceDuration: 10,
      selectedDate: defaultDateString,
      loading: false,
      currentRaceIndex: 0,
      successMessage: null,
      errorMessage: null,
      progressMessages: [],
      apiEndpoint: 'https://app.liverunapp.com/api/races',
      channels: {}
    }
  },
  computed: {
    ...mapGetters({
      trainer: 'Trainer/trainer'
    }),
    formattedSelectedDate() {
      if (!this.selectedDate) return null
      const date = new Date(this.selectedDate + 'T00:00:00')
      return date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      })
    }
  },
  created() {
    // Update current time every second
    setInterval(() => {
      this.currentTime = new Date().toLocaleTimeString()
    }, 1000)
    // Fetch audio channels
    this.getExistingChannels()
  },
  methods: {
    ...mapActions({
      createRace: 'Race/createOne'
    }),

    async createTestRaces() {
      if (!this.numberOfRaces || !this.raceDuration || !this.selectedDate) {
        this.errorMessage = "Please fill in number of races, duration, and select a date"
        return
      }

      if (!this.trainer || !this.trainer.id) {
        this.errorMessage = "Trainer information not loaded. Please refresh the page."
        return
      }

      this.loading = true
      this.errorMessage = null
      this.successMessage = null
      this.progressMessages = []
      this.currentRaceIndex = 0

      const totalRaces = parseInt(this.numberOfRaces)
      let successCount = 0
      let errorCount = 0

      for (let i = 1; i <= totalRaces; i++) {
        this.currentRaceIndex = i
        
        try {
          const raceData = this.generateRaceData(i)
          console.log(`🏁 Creating race ${i}/${totalRaces}:`, raceData)
          
          const response = await this.createRace(raceData)
          
          // Save to Firebase after race creation
          if (response.data && response.data.id) {
            this.addValueInFirebase(response.data, response.data.id)
          }
          
          this.progressMessages.push(`✅ Race ${i}: "${raceData.name}" created successfully (ID: ${response.data.id})`)
          successCount++
          
          console.log(`✅ Race ${i} created:`, response.data)
          
          // Small delay between requests to avoid overwhelming the server
          await new Promise(resolve => setTimeout(resolve, 500))
          
        } catch (error) {
          console.error(`❌ Error creating race ${i}:`, error)
          this.progressMessages.push(`❌ Race ${i}: Failed to create - ${error.message || 'Unknown error'}`)
          errorCount++
        }
      }

      this.loading = false
      this.currentRaceIndex = 0

      if (successCount === totalRaces) {
        this.successMessage = `🎉 Successfully created all ${totalRaces} test races!`
      } else if (successCount > 0) {
        this.successMessage = `⚠️ Created ${successCount}/${totalRaces} races (${errorCount} failed)`
      } else {
        this.errorMessage = `❌ Failed to create any races. Check console for details.`
      }

      // Refresh the races list
      this.$parent.getRaces()
    },

    generateRaceData(index) {
      // Generate random time: hours (0-23), minutes (0-59), seconds (0-59)
      const randomHours = Math.floor(Math.random() * 24)
      const randomMinutes = Math.floor(Math.random() * 60)
      const randomSeconds = Math.floor(Math.random() * 60)
      
      // Format time as HH:MM:SS with leading zeros
      const timeString = `${String(randomHours).padStart(2, '0')}:${String(randomMinutes).padStart(2, '0')}:${String(randomSeconds).padStart(2, '0')}`
      
      // Use the selected date with random time, convert to ISO string format
      const raceDate = new Date(this.selectedDate + 'T' + timeString)
      
      // Use channel C for audio, fallback to hardcoded URL if channel C is not available
      const audioChannel = this.channels.audioChannelC || "https://studio29.radiolize.com/radio/8000/radio.mp3"
      
      // Generate unique seed for placeholder images to ensure each race gets a different image
      const uniqueSeed = Date.now() + index + Math.floor(Math.random() * 10000)
      
      // Generate random placeholder images using Lorem Picsum API
      // Action image: 50x50px (small square for floating button)
      const actionImageUrl = `https://picsum.photos/seed/${uniqueSeed}/50/50`
      // Race banner image: 1200x400px (3:1 aspect ratio matching app banner display)
      const imageUrl = `https://picsum.photos/seed/${uniqueSeed + 1000}/1200/400`
      
      return {
        name: `TEST_DO_NOT_JOIN_${index}`,
        description: "Test race created by debug tool - DO NOT JOIN",
        notes: "Generated by debug race creator",
        date: raceDate.toISOString(),
        startDate: "",
        finishDate: "",
        type: "LIVE",
        secondType: "RUN_OR_WALK",
        thirdType: null,
        mobileChannelToPlay: audioChannel,
        language: "en",
        imageUrl: imageUrl,
        audioFileUrl: "",
        rsvp_limit: 1000,
        randomClubTag: false,
        endSessionUrl: "https://liverunapp.com",
        actionImageUrl: actionImageUrl,
        end_interval: "",
        tags: [],
        duration: this.raceDuration.toString(),
        trainer: this.trainer
      }
    },

    clearMessages() {
      this.successMessage = null
      this.errorMessage = null
      this.progressMessages = []
    },

    async getExistingChannels() {
      try {
        const channels = await SettingsService.get()
        if (channels && channels.data) {
          this.channels = channels.data
        }
      } catch (error) {
        console.error('Error fetching channels:', error)
      }
    },

    addValueInFirebase(respData, raceId) {
      db.ref(`/${this.$fbDbEntry}/${raceId}`).update({
        'streamingUrl': respData.mobileChannelToPlay,
        'endSessionUrl': respData.endSessionUrl //Add Action url in Firebase
      }).catch(error => {
        console.log("Firebase error--", error)
      })
    },

    clearAll() {
      this.numberOfRaces = 5
      this.raceDuration = 10
      // Reset to default date (6 months from today)
      const defaultDate = new Date()
      defaultDate.setMonth(defaultDate.getMonth() + 6)
      this.selectedDate = defaultDate.toISOString().split('T')[0]
      this.clearMessages()
    }
  }
}
</script>

<style scoped>
.debug-race-creator {
  margin-top: 2rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 2px dashed #6c757d;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.debug-race-creator__header {
  text-align: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #dee2e6;
}

.debug-race-creator__header h3 {
  margin: 0;
  color: #495057;
  font-size: 1.5rem;
  font-weight: 600;
}

.debug-race-creator__subtitle {
  margin: 0.5rem 0 0 0;
  color: #6c757d;
  font-size: 0.9rem;
  font-style: italic;
}

.debug-race-creator__content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
}

.debug-section {
  background: white;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border-left: 4px solid #007bff;
}

.debug-section h4 {
  margin: 0 0 1rem 0;
  color: #495057;
  font-size: 1.1rem;
  font-weight: 500;
}

.debug-form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

.debug-form-group {
  margin-bottom: 1rem;
}

.debug-form-group--full {
  grid-column: 1 / -1;
}

.debug-form-group .debug-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
}

.debug-input {
  width: 100%;
  padding: 0.5rem;
  border: 2px solid #e9ecef;
  border-radius: 6px;
  font-size: 0.9rem;
  transition: border-color 0.2s ease;
}

.debug-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.debug-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.debug-btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.debug-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

.debug-btn:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

.debug-btn--primary {
  background: #007bff;
  color: white;
}

.debug-btn--primary:not(:disabled):hover {
  background: #0056b3;
}

.debug-btn--secondary {
  background: #6c757d;
  color: white;
}

.debug-btn--secondary:not(:disabled):hover {
  background: #545b62;
}

.debug-alert {
  margin-top: 1rem;
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
}

.debug-alert--success {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.debug-alert--error {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.debug-progress {
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #dee2e6;
  max-height: 300px;
  overflow-y: auto;
}

.debug-progress h5 {
  margin: 0 0 0.5rem 0;
  color: #495057;
  font-size: 0.9rem;
}

.debug-progress-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.debug-progress-item {
  padding: 0.25rem 0;
  font-size: 0.8rem;
  font-family: 'Courier New', monospace;
  border-bottom: 1px solid #e9ecef;
}

.debug-progress-item:last-child {
  border-bottom: none;
}

.debug-config-preview {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.debug-config-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.debug-config-label {
  font-weight: 600;
  color: #495057;
  font-size: 0.85rem;
  min-width: 100px;
}

.debug-config-value {
  font-family: 'Courier New', monospace;
  color: #6c757d;
  font-size: 0.8rem;
  text-align: right;
  flex: 1;
  margin-left: 1rem;
}

.debug-config-value--small {
  font-size: 0.7rem;
  word-break: break-all;
}

.debug-info {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.debug-info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.debug-info-item .debug-label {
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
}

.debug-value {
  font-family: 'Courier New', monospace;
  color: #6c757d;
  font-size: 0.85rem;
}

.debug-value--active {
  color: #28a745;
  font-weight: 600;
}

.debug-value--small {
  font-size: 0.7rem;
  word-break: break-all;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .debug-race-creator__content {
    grid-template-columns: 1fr;
  }
  
  .debug-form-row {
    grid-template-columns: 1fr;
  }
  
  .debug-buttons {
    flex-direction: column;
  }
  
  .debug-btn {
    width: 100%;
    text-align: center;
  }
  
  .debug-config-item {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .debug-config-value {
    margin-left: 0;
    margin-top: 0.25rem;
    text-align: left;
  }
}
</style>



// WEBPACK FOOTER //
// DebugRaceCreator.vue?bb577152